import React, {useCallback} from "react";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Typography,
  ModalFooter,
  ButtonGroup
} from "@one-thd/sui-atomic-components";
import { WebAuthnIconsActiveLarge } from "./index";
import {
  setNativeWebAuthnEnrollmentSuccess,
  setWebauthnNoThanksNative,
  setWebauthnNoThanksWeb,
  clearWebAuthnInfoNative,
} from "../../../../utils/webauthnUtils";
import { useCommonContext } from "../../CommonProvider"
import { MESSAGE, MESSAGE_TYPE } from "../../../../constants/messages";
import { OtpLoadingView } from "../../../NewVerifyOtpModal/OtpModalViews";
import { extractCredentialIdFromAttestationResult, setWebauthnEnrollmentSuccess } from "../../../../utils/webauthnUtils"
import { submitWebAuthnEnrollment } from "../../../../api/custAccountApi";
import newRelic from "../../../../utils/newrelic";
import analytics from "../../../../utils/analytics";
import { getErrorCode } from "../../../../utils/errors";
import { useNativeWebAuthnData } from "../../NativeWebAuthnProvider";
import {isNativeApp, isWebauthnEnabledForNativeAppUsers, postDataToEventBus, subscribeToEvent} from "../../../../utils";

const CompletionMessageComponent = ({handleClosePreviousModal, email, setStale, frState}) => {
  const [modelOpen, setModalOpen] = React.useState(true);
  const [modalLoading, setModalLoading] = React.useState(false);
  const webAuthRef = React.useRef();
  const {addAlert} = useCommonContext();
  const { nativeWebAuthnData, setNativeWebAuthnData } = useNativeWebAuthnData();


  const handleClose = () => {
    setModalOpen(false);
    handleClosePreviousModal();
    setStale();
    addAlert(MESSAGE_TYPE.SUCCESS, MESSAGE.SUCCESS_PASSWORDLESS_ENROLLMENT );
  }

  const handleCloseOnError = (error="GEN_ERR_500") => {
    setModalOpen(false);
    handleClosePreviousModal();
    setStale();
    addAlert(MESSAGE_TYPE.DANGER, MESSAGE.WEBAUTHN_FAILURE_MESSAGE );
    const errorCode = getErrorCode(error);
    analytics.postErrorEvent("Security Page: Enrollment WebAuthn", errorCode, MESSAGE.WEBAUTHN_FAILURE_MESSAGE);
  }

  const handleCloseOnSuccess = () => {
    setModalOpen(false);
    handleClosePreviousModal();
    setStale();
    addAlert(MESSAGE_TYPE.SUCCESS, MESSAGE.SUCCESSFUL_ENROLLMENT_WEBAUTHN_ALERT );
    analytics.postSettingsSubmissionEventWebauthn();
  }

  const renderBodyView = (modalState) => {
    if(modalState) {
      return  (
        <div className='container-for-loading'>
          <OtpLoadingView/>
        </div>
      )
    } else {
      return (
        <>
          <Typography variant="body-base">
                  You can now sign in to your account with a one time passcode. To sign in even faster use your device face or fingerprint recognition for future sign-ins. This is the suggested option.
          </Typography>
          { WebAuthnIconsActiveLarge }
        </>
      )
    }
  }

  const renderTitle = (loadingState) => {
    if(loadingState) {
      return "Follow your device instructions..."
    }
    return "Complete!"
  }

  const startLoading = () => {
    return setModalLoading(true);
  }

  const stopLoading = () => {
    return setModalLoading(false);
  }

  const handleTheAcceptanceWebAuthn = () => {
    analytics.postSubmissionEvent("biometric enrollment start");
    startLoading();
    if (isNativeApp()) {
      newRelic.nativeAppWebAuthnUpgradeStart("WEBAUTHN_ENROLLMENT_INIT");
      // Send message to native app to start the WebAuthn enrollment on their side
      postDataToEventBus("WEBAUTHN_ENROLLMENT_INIT");
      // Listen for the result of the native enrollment
      subscribeToEvent("SEND_WEBAUTHN_RESULT", (payload) => {
        newRelic.nativeAppWebAuthnSubscription("SEND_WEBAUTHN_RESULT", payload);
        const { success, frUuid, error } = payload;
        if (success) {
          submitWebAuthnEnrollment(null, frUuid);
          const newWebAuthnData = setNativeWebAuthnEnrollmentSuccess(nativeWebAuthnData, email, frUuid);
          setNativeWebAuthnData(newWebAuthnData);
          stopLoading();
          setStale();
          addAlert(MESSAGE_TYPE.SUCCESS, MESSAGE.SUCCESSFUL_ENROLLMENT_WEBAUTHN_ALERT );
          handleCloseOnSuccess();
          analytics.postSettingsSubmissionEventWebauthn();
          newRelic.nativeAppWebAuthnSuccess("WEBAUTHN_ENROLLMENT_SUCCESS");
        } else {
          stopLoading();
          handleCloseOnError();
          newRelic.nativeAppWebAuthnFailure("WEBAUTHN_ENROLLMENT_FAILURE", error);
          clearWebAuthnInfoNative(email, nativeWebAuthnData, setNativeWebAuthnData);
        }
      })
    } else {
      const webAuthEnrollJSPayload = frState.callbacks?.[0]?.output[0]?.value;
      // eslint-disable-next-line no-new-func
      Function(webAuthEnrollJSPayload)();
    }

    // Posting to New Relic
    newRelic.logWebAuthnStartingFlow("WebAuthn Enrollment Flow")
  }

  const postWebAuthClick = () => {
    const webAuthnOutcome = webAuthRef.current?.value;
    frState.callbacks[2].input[0].value = webAuthnOutcome;
    if (webAuthnOutcome?.includes("ERROR::")) {
      stopLoading();
      handleCloseOnError();
      newRelic.logWebAuthnFailure({webAuthnOutcome}, `WebAuthn Enrollment Flow: Script executed and failed: ${webAuthnOutcome}`)
    } else {
      const webAuthnDeviceId = extractCredentialIdFromAttestationResult(webAuthnOutcome);
      submitWebAuthnEnrollment(frState, webAuthnDeviceId)
        .then(({response, error}) => {
          if(response) {
            stopLoading();
            setWebauthnEnrollmentSuccess( email, webAuthnDeviceId, frState );
            handleCloseOnSuccess();
            newRelic.logWebAuthnSuccess("Flow: WebAuthn Enrollment");
          } else if (error) {
            stopLoading();
            handleCloseOnError(error);
            newRelic.logWebAuthnFailure({error}, `WebAuthn Enrollment Flow: BE sent an error response: ${error}`);
          }
        })
        .catch(() => {
          stopLoading();
          handleCloseOnError();
          newRelic.logWebAuthnFailure({webAuthnOutcome}, `WebAuthn Enrollment Flow: Request Failed: ${webAuthnOutcome}`)
        });
    }
  }

  const onWebAuthnNoThanks = useCallback(() => {
    if (!isNativeApp()) {
      setWebauthnNoThanksWeb(email);
    } else if (isWebauthnEnabledForNativeAppUsers()) {
      setWebauthnNoThanksNative(email, nativeWebAuthnData, setNativeWebAuthnData);
    }
    handleClose();
  }, [email, nativeWebAuthnData, setNativeWebAuthnData]);

  return (
        <Modal open={modelOpen} onClose={handleClose}>
            <ModalHeader onClose={handleClose}>{ renderTitle(modalLoading) }</ModalHeader>
            <ModalBody>
                { renderBodyView(modalLoading) }
            </ModalBody>
            <ModalFooter position="end">

                  { !modalLoading && (
                            <ButtonGroup orientation="vertical">
                            <Button onClick={ handleTheAcceptanceWebAuthn } fullWidth variant="primary">Enable Face or Fingerprint ID</Button>
                            <div className="container-for-btns-completion-modal">
                            <Button onClick={onWebAuthnNoThanks} fullWidth variant="text">No thanks</Button>
                            </div>
                        </ButtonGroup>
                  )}

            </ModalFooter>
            <div id="webAuthnOutcome" ref={webAuthRef} />
            <input id="loginButton_0" type="button" onClick={postWebAuthClick} />
        </Modal>
  );
}

export default CompletionMessageComponent;